import { createSelector } from "reselect"

import { EmployeeHolidayRecord, EmployeePTORecord } from "../cached-data/types"
import { ReduxState } from "../common/types"

const ptoObjectsSelector = (state: ReduxState) => state.entities.employeePTO?.objects ?? {}
const holidayObjectsSelector = (state: ReduxState) => state.entities.employeeHolidays?.objects ?? {}
const tkSummarySelector = (state: ReduxState) => state.exceptions.tkSummary

/**
 * Compute fulfillment for every PTO and holiday record in the store, using the
 * current {@link TkSummary} data as the source of truth.
 *
 * Returns an empty object when `tkSummary` has not yet been fetched.
 *
 * Fulfillment rules:
 *   - **ABSENCE_TYPE**: a tkSummary row for the employee on the record's date has the
 *     record's `absence_type_id` in its `absence_type_ids` array.
 *   - **COST_CODE**: the sum of `st` minutes across rows for the employee on the date
 *     whose `cost_code.pto_bucket_id` matches `pto_bucket_id` is ≥ `hours × 60`.
 *     Records with null or zero `hours` are never fulfilled.
 *
 * @returns A map of record ID (string) → fulfilled boolean covering both PTO and
 *   holiday records. IDs do not overlap between the two entity types.
 */
export const selectPTOFulfillment = createSelector(
    ptoObjectsSelector,
    holidayObjectsSelector,
    tkSummarySelector,
    (ptoObjects, holidayObjects, tkSummary): Record<string, boolean> => {
        if (!tkSummary) return {}

        const result: Record<string, boolean> = {}
        const allRecords: Array<EmployeePTORecord | EmployeeHolidayRecord> = [
            ...Object.values(ptoObjects),
            ...Object.values(holidayObjects),
        ]

        for (const record of allRecords) {
            const dateRows = (tkSummary[String(record.employee)] ?? []).filter(
                row => row.shift_date === record.date
            )

            let fulfilled = false
            if (record.pto_bucket_type === "ABSENCE_TYPE") {
                const absenceTypeId = record.absence_type_id
                if (absenceTypeId !== null) {
                    fulfilled = dateRows.some(row => row.absence_type_ids?.includes(absenceTypeId))
                }
            } else if (record.pto_bucket_type === "COST_CODE") {
                if (record.hours !== null && record.hours > 0) {
                    const totalSt = dateRows
                        .filter(row => row.cost_code?.pto_bucket_id === record.pto_bucket_id)
                        .reduce((sum, row) => sum + (row.st ?? 0), 0)
                    fulfilled = totalSt / 60 >= record.hours
                }
            }

            result[String(record.id)] = fulfilled
        }

        return result
    }
)
